import React from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { UtilityFunctions } from "../../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../../utils/constants/stringConstants";
import "./GetStarted.css";

type Product = {
  key: string;
  name: string;
  desc: string;
  route: string;
  cta: string;
};

// Only products with a real 2026 signup page live here.
// The legacy jobs flow (/signup) is intentionally NOT listed.
const PRODUCTS: Product[] = [
  {
    key: "invoice_chaser",
    name: "Invoice Chaser",
    desc: "AI calls your customers with outstanding invoices and gets you paid faster.",
    route: "/accounts/signup",
    cta: "Get started",
  },
  {
    key: "interview_simulator",
    name: "Interview Simulator",
    desc: "Practice interviews with an AI avatar, or run unbiased hiring interviews at scale.",
    route: "/interview-simulator/signup",
    cta: "Get started",
  },
  {
    key: "legal_assist",
    name: "Legal Assist",
    desc: "Instant, personalised legal guidance built for Australian businesses.",
    route: "/legal-assist/signup",
    cta: "Get started",
  },
  {
    key: "hr_simulator",
    name: "HR Simulator",
    desc: "Expert HR guidance around the clock, without a dedicated HR department.",
    route: "/hr-simulator/signup",
    cta: "Get started",
  },
];

interface GetStartedProps extends RouteComponentProps<any> {}

const GetStarted: React.FC<GetStartedProps> = (props) => {
  const choose = (product: Product) => {
    props.history.push(product.route);
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      fontFamily: "'DM Sans', sans-serif",
      backgroundColor: "#F8F9FA",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 20px",
    },
    logoChip: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#0065FF",
      borderRadius: "10px",
      padding: "10px 18px",
      marginBottom: "24px",
      cursor: "pointer",
    },
    logo: {
      height: "28px",
    },
    title: {
      fontFamily: "'Urbanist', sans-serif",
      fontWeight: 700,
      fontSize: "28px",
      lineHeight: 1.2,
      color: "#141414",
      marginBottom: "8px",
      textAlign: "center",
    },
    subtitle: {
      fontSize: "16px",
      color: "#555555",
      marginBottom: "40px",
      textAlign: "center",
      maxWidth: "520px",
    },
    footerText: {
      marginTop: "40px",
      fontSize: "14px",
      color: "#555555",
      textAlign: "center",
    },
    footerLink: {
      color: "#0065FF",
      fontWeight: 600,
      textDecoration: "none",
    },
  };

  return (
    <>
      <Helmet>
        <title>Get Started - MPLOI</title>
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Urbanist:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div style={styles.container}>
        <span
          style={styles.logoChip}
          onClick={() => props.history.push(STRING_CONSTANTS.navigationString.index)}
        >
          <img
            alt="MPLOI"
            style={styles.logo}
            src={UtilityFunctions.getImageURL() + "logo_White.svg"}
          />
        </span>

        <h1 style={styles.title}>Create your MPLOI account</h1>
        <p style={styles.subtitle}>
          Choose the product you'd like to start with. You can add the others to your
          account at any time.
        </p>

        <div className="gs-grid">
          {PRODUCTS.map((product) => (
            <button
              key={product.key}
              type="button"
              className="gs-card"
              onClick={() => choose(product)}
              aria-label={`Sign up for ${product.name}`}
            >
              <span className="gs-card-name">{product.name}</span>
              <span className="gs-card-desc">{product.desc}</span>
              <span className="gs-card-cta">{product.cta}</span>
            </button>
          ))}
        </div>

        <p style={styles.footerText}>
          Already have an account?{" "}
          <Link to={STRING_CONSTANTS.navigationString.login} style={styles.footerLink}>
            Sign In
          </Link>
        </p>
      </div>
    </>
  );
};

export default withRouter(GetStarted);
